import { useMemo, useState } from 'react';
import type { Goal, Proof, Role } from '@/lib/postIdeas';
import { GOALS, PAGE_SIZE, PROOFS, ROLES, angleText, fillHook, guardrails, selectAngles } from '@/lib/postIdeas';

function IdeaCard({ angle, roleFit, topic }: { angle: ReturnType<typeof selectAngles>[number]['angle']; roleFit: boolean; topic: string }) {
  const [copied, setCopied] = useState(false);
  const proof = PROOFS.find((entry) => entry.id === angle.proof);

  const copy = () => {
    try {
      navigator.clipboard
        .writeText(angleText(angle, topic))
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste inerte.
        });
    } catch {
      // Pas de Clipboard API : on n'interrompt rien.
    }
  };

  return (
    <li className="nz-ideas__card">
      <div className="nz-ideas__meta">
        <span className="nz-sim__badge">{proof?.label}</span>
        {roleFit ? null : <span className="nz-sim__badge">À adapter à votre rôle</span>}
      </div>
      <h3 className="nz-ideas__name">{angle.name}</h3>
      <p className="nz-ideas__hook">{fillHook(angle.hook, topic)}</p>
      <ol className="nz-ideas__steps">
        {angle.structure.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="nz-ideas__trap">
        <strong>Le piège : </strong>
        {angle.trap}
      </p>
      <div className="nz-wizard__actions">
        <button type="button" className="nz-btn-secondary" onClick={copy}>
          {copied ? 'Copié' : 'Copier le canevas'}
        </button>
      </div>
    </li>
  );
}

export function PostIdeas() {
  const [role, setRole] = useState<Role | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [proofs, setProofs] = useState<Proof[]>([]);
  const [topic, setTopic] = useState('');
  const [shown, setShown] = useState(PAGE_SIZE);

  const selection = useMemo(() => ({ role, goal, proofs }), [role, goal, proofs]);
  const ranked = useMemo(() => selectAngles(selection), [selection]);
  const notes = useMemo(() => guardrails(selection), [selection]);

  const pickRole = (id: Role) => {
    setRole(role === id ? null : id);
    setShown(PAGE_SIZE);
  };
  const pickGoal = (id: Goal) => {
    setGoal(goal === id ? null : id);
    setShown(PAGE_SIZE);
  };
  const toggleProof = (id: Proof) => {
    setProofs(proofs.includes(id) ? proofs.filter((p) => p !== id) : [...proofs, id]);
    setShown(PAGE_SIZE);
  };
  const reset = () => {
    setRole(null);
    setGoal(null);
    setProofs([]);
    setTopic('');
    setShown(PAGE_SIZE);
  };

  const filtered = role !== null || goal !== null || proofs.length > 0 || topic !== '';

  return (
    <div className="nz-sim nz-ideas">
      <fieldset className="nz-ideas__group">
        <legend className="nz-sim__label">Vous êtes</legend>
        <div className="nz-ideas__chips">
          {ROLES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={role === entry.id}
              className={`nz-wizard__option nz-ideas__chip${role === entry.id ? ' nz-wizard__option--on' : ''}`}
              onClick={() => pickRole(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="nz-ideas__group">
        <legend className="nz-sim__label">Votre objectif</legend>
        <div className="nz-ideas__chips">
          {GOALS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={goal === entry.id}
              className={`nz-wizard__option nz-ideas__chip${goal === entry.id ? ' nz-wizard__option--on' : ''}`}
              onClick={() => pickGoal(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="nz-ideas__group">
        <legend className="nz-sim__label">Ce que vous avez en main (plusieurs choix possibles)</legend>
        <div className="nz-ideas__chips">
          {PROOFS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-pressed={proofs.includes(entry.id)}
              className={`nz-wizard__option nz-ideas__chip${proofs.includes(entry.id) ? ' nz-wizard__option--on' : ''}`}
              onClick={() => toggleProof(entry.id)}
            >
              {entry.label}
              <small>{entry.hint}</small>
            </button>
          ))}
        </div>
      </fieldset>

      <label className="nz-sim__label" htmlFor="nz-ideas-topic">
        Votre sujet (facultatif)
      </label>
      <input
        id="nz-ideas-topic"
        className="nz-sim__input"
        type="text"
        value={topic}
        maxLength={80}
        placeholder="Par exemple : la prospection à froid, notre passage à 4 jours, le recrutement de développeurs"
        onChange={(event) => setTopic(event.target.value)}
      />

      <div className="nz-sim__bar">
        <span className="nz-ideas__count" aria-live="polite">
          {ranked.length} angle{ranked.length > 1 ? 's' : ''} sur 28
        </span>
        {filtered ? (
          <button type="button" className="nz-wizard__back nz-sim__sample" onClick={reset}>
            Tout effacer
          </button>
        ) : null}
      </div>

      {notes.length > 0 ? (
        <div className="nz-wizard__tip nz-ideas__notes">
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      ) : null}

      {ranked.length === 0 ? (
        <p className="nz-ideas__empty">Aucun angle ne correspond : retirez un filtre pour élargir.</p>
      ) : (
        <ul className="nz-ideas__list">
          {ranked.slice(0, shown).map(({ angle, roleFit }) => (
            <IdeaCard key={angle.id} angle={angle} roleFit={roleFit} topic={topic} />
          ))}
        </ul>
      )}

      {ranked.length > shown ? (
        <div className="nz-wizard__actions">
          <button type="button" className="nz-btn-secondary" onClick={() => setShown(shown + PAGE_SIZE)}>
            Voir {Math.min(PAGE_SIZE, ranked.length - shown)} angle{ranked.length - shown > 1 ? 's' : ''} de plus
          </button>
        </div>
      ) : null}
    </div>
  );
}
